/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiUserPlus, FiEdit2, FiTrash2, FiX, FiChevronDown, FiChevronUp, FiUserCheck, FiUserX } from "react-icons/fi";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { useUI } from "../../theme/ThemeProvider";
import ResponsiveTable from "../../components/ResponsiveTable";

// Using colors from useUI instead of hardcoded COLORS
// ✅ FORCE SweetAlert to be always on top of this page only
if (!document.getElementById("swal-fix-admin-employees")) {
  const style = document.createElement("style");
  style.id = "swal-fix-admin-employees";
  style.innerHTML = `
    .swal-above-modal {
      z-index: 1000000 !important;
      position: fixed !important;
    }
    .swal2-container {
      z-index: 1000000 !important;
    }
  `;
  document.head.appendChild(style);
}

export default function AdminEmployees() {
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, '');
  const tenantId = parseInt(localStorage.getItem("tenantId") || "0");
  console.log(tenantId);


  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openAccordion, setOpenAccordion] = useState({});
  const [formKey, setFormKey] = useState(0);


  const [formData, setFormData] = useState({
    userId: 0,
    name: "",
    engName: "",
    email: "",
    password: "",
    isActive: 1,
    userTypeID: 1,
    userFunctionID: 1,
    // Permissions - all default to 0
    // Orders
    viewOrders: 0,
    // Products
    viewProducts: 0,
    createProduct: 0,
    editProduct: 0,
    deleteProduct: 0,
    // Stock
    viewStock: 0,
    // Divisions
    viewDivisions: 0,
    addDivisions: 0,
    editDivisions: 0,
    deleteDivisions: 0,
    // Categories
    viewCategories: 0,
    addCategories: 0,
    editCategories: 0,
    deleteCategories: 0,
    // Customers
    viewCustomers: 0,
    addCustomers: 0,
    editCustomers: 0,
    deleteCustomers: 0,
    // Banners
    viewBanners: 0,
    addBanners: 0,
    editBanners: 0,
    deleteBanners: 0,
    // Theme
    viewTheme: 0,
    editTheme: 0,
    // Store Settings
    viewStoreSettings: 0,
    editStoreSettings: 0,
    // Coupons
    viewCoupons: 0,
    addCoupons: 0,
    editCoupons: 0,
    deleteCoupons: 0,
    // Activity
    viewActivity: 0,
    // Subscriptions
    viewSubscriptions: 0,
    editSubscriptions: 0,
    // Notification
    viewNotification: 0,
    addNotification: 0,
    // Payment Methods
    viewPaymentMethods: 0,
    addPaymentMethods: 0,
    editPaymentMethods: 0,
    deletePaymentMethods: 0,
    // Shipping
    viewShipping: 0,
    addShipping: 0,
    editShipping: 0,
    deleteShipping: 0,
    // Invoice
    viewInvoice: 0,
    editInvoice: 0,
    // Analytics
    viewAnalytics: 0,
    // Sales Report
    viewSalesReport: 0,
    // Inventory Report
    viewInventoryReport: 0,
    // Customer Report
    viewCustomerReport: 0,
    // Product Report
    viewProductReport: 0,
    // Employees
    viewEmployees: 0,
    addEmployees: 0,
    editEmployees: 0,
    deleteEmployees: 0,
  });

  useEffect(() => {
    if (tenantId && tenantId > 0) {
      fetchEmployees();
    }
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const endpoint = `${baseUrl}/TenantUser?UserID=-1&TenantId=${tenantId}`;

      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();

      if (!Array.isArray(data)) {
        setEmployees([]);
        return;
      }

      const employeesList = data
        .filter(user => {
          // نستثني أي يوزر:
          // 1. UserID = 1 (يوزر وهمي موجود في كل المتاجر)
          // 2. الاسم فاضي تمامًا
          // 3. IsDeleted = 1 (محذوف منطقيًا)
          // 4. Email فاضي و Username فاضي (تأكيد إنه يوزر وهمي)
          const isFakeUser = false;
          const isEmptyName = !user.Username?.trim() && !user.EngUsername?.trim();
          const isDeleted = user.IsDeleted === 1 || user.isDeleted === 1;
          const isEmptyEmail = !user.Email?.trim();

          return !(isFakeUser || (isEmptyName && isEmptyEmail) || isDeleted);
        })
        .map(user => ({
          id: Number(user.UserID),
          name: (user.Username || "").trim(),
          engName: (user.EngUsername || "").trim(),
          email: (user.Email || "").trim(),
          active: user.IsActive === 1 || user.IsActive === true,
          userTypeID: Number(user.UserTypeID || 1),
          userFunctionID: Number(user.UserFunctionID || 1),
          userData: {
            // Orders
            viewOrders: Number(user.ViewOrders ?? 0),
            // Products
            viewProducts: Number(user.ViewProducts ?? 0),
            createProduct: Number(user.CreateProduct ?? 0),
            editProduct: Number(user.EditProduct ?? 0),
            deleteProduct: Number(user.DeleteProduct ?? 0),
            // Stock
            viewStock: Number(user.ViewStock ?? 0),
            // Divisions
            viewDivisions: Number(user.ViewDivisions ?? 0),
            addDivisions: Number(user.AddDivisions ?? 0),
            editDivisions: Number(user.EditDivisions ?? 0),
            deleteDivisions: Number(user.DeleteDivisions ?? 0),
            // Categories
            viewCategories: Number(user.ViewCategories ?? 0),
            addCategories: Number(user.AddCategories ?? 0),
            editCategories: Number(user.EditCategories ?? 0),
            deleteCategories: Number(user.DeleteCategories ?? 0),
            // Customers
            viewCustomers: Number(user.ViewCustomers ?? 0),
            addCustomers: Number(user.AddCustomers ?? 0),
            editCustomers: Number(user.EditCustomers ?? 0),
            deleteCustomers: Number(user.DeleteCustomers ?? 0),
            // Banners
            viewBanners: Number(user.ViewBanners ?? 0),
            addBanners: Number(user.AddBanners ?? 0),
            editBanners: Number(user.EditBanners ?? 0),
            deleteBanners: Number(user.DeleteBanners ?? 0),
            // Theme
            viewTheme: Number(user.ViewTheme ?? 0),
            editTheme: Number(user.EditTheme ?? 0),
            // Store Settings
            viewStoreSettings: Number(user.ViewStoreSettings ?? 0),
            editStoreSettings: Number(user.EditStoreSettings ?? 0),
            // Coupons
            viewCoupons: Number(user.ViewCoupons ?? 0),
            addCoupons: Number(user.AddCoupons ?? 0),
            editCoupons: Number(user.EditCoupons ?? 0),
            deleteCoupons: Number(user.DeleteCoupons ?? 0),
            // Activity
            viewActivity: Number(user.ViewActivity ?? 0),
            // Subscriptions
            viewSubscriptions: Number(user.ViewSubscriptions ?? 0),
            editSubscriptions: Number(user.EditSubscriptions ?? 0),
            // Notification
            viewNotification: Number(user.ViewNotification ?? 0),
            addNotification: Number(user.AddNotification ?? 0),
            // Payment Methods
            viewPaymentMethods: Number(user.ViewPaymentMethods ?? 0),
            addPaymentMethods: Number(user.AddPaymentMethods ?? 0),
            editPaymentMethods: Number(user.EditPaymentMethods ?? 0),
            deletePaymentMethods: Number(user.DeletePaymentMethods ?? 0),
            // Shipping
            viewShipping: Number(user.ViewShipping ?? 0),
            addShipping: Number(user.AddShipping ?? 0),
            editShipping: Number(user.EditShipping ?? 0),
            deleteShipping: Number(user.DeleteShipping ?? 0),
            // Invoice
            viewInvoice: Number(user.ViewInvoice ?? 0),
            editInvoice: Number(user.EditInvoice ?? 0),
            // Analytics
            viewAnalytics: Number(user.ViewAnalytics ?? 0),
            // Sales Report
            viewSalesReport: Number(user.ViewSalesReport ?? 0),
            // Inventory Report
            viewInventoryReport: Number(user.ViewInventoryReport ?? 0),
            // Customer Report
            viewCustomerReport: Number(user.ViewCustomerReport ?? 0),
            // Product Report
            viewProductReport: Number(user.ViewProductReport ?? 0),
            // Employees
            viewEmployees: Number(user.ViewEmployees ?? 0),
            addEmployees: Number(user.AddEmployees ?? 0),
            editEmployees: Number(user.EditEmployees ?? 0),
            deleteEmployees: Number(user.DeleteEmployees ?? 0),
          },
        }));

      setEmployees(employeesList);
      console.log("تم جلب الموظفين بنجاح (بعد الفلترة):", employeesList);

    } catch (err) {
      console.error("فشل جلب الموظفين:", err);
      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل تحميل الموظفين" : "Failed to load employees",
        text: err.message,
        confirmButtonColor: "#7400BF",
        customClass: { container: 'swal-above-modal' },
      });
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setFormData({
      userId: 0,
      name: "",
      engName: "",
      email: "",
      password: "",
      isActive: 1,
      userTypeID: 1,
      userFunctionID: 1,
      // ALL permissions reset to 0
      // Orders
      viewOrders: 0,
      // Products
      viewProducts: 0,
      createProduct: 0,
      editProduct: 0,
      deleteProduct: 0,
      // Stock
      viewStock: 0,
      // Divisions
      viewDivisions: 0,
      addDivisions: 0,
      editDivisions: 0,
      deleteDivisions: 0,
      // Categories
      viewCategories: 0,
      addCategories: 0,
      editCategories: 0,
      deleteCategories: 0,
      // Customers
      viewCustomers: 0,
      addCustomers: 0,
      editCustomers: 0,
      deleteCustomers: 0,
      // Banners
      viewBanners: 0,
      addBanners: 0,
      editBanners: 0,
      deleteBanners: 0,
      // Theme
      viewTheme: 0,
      editTheme: 0,
      // Store Settings
      viewStoreSettings: 0,
      editStoreSettings: 0,
      // Coupons
      viewCoupons: 0,
      addCoupons: 0,
      editCoupons: 0,
      deleteCoupons: 0,
      // Activity
      viewActivity: 0,
      // Subscriptions
      viewSubscriptions: 0,
      editSubscriptions: 0,
      // Notification
      viewNotification: 0,
      addNotification: 0,
      // Payment Methods
      viewPaymentMethods: 0,
      addPaymentMethods: 0,
      editPaymentMethods: 0,
      deletePaymentMethods: 0,
      // Shipping
      viewShipping: 0,
      addShipping: 0,
      editShipping: 0,
      deleteShipping: 0,
      // Invoice
      viewInvoice: 0,
      editInvoice: 0,
      // Analytics
      viewAnalytics: 0,
      // Sales Report
      viewSalesReport: 0,
      // Inventory Report
      viewInventoryReport: 0,
      // Customer Report
      viewCustomerReport: 0,
      // Product Report
      viewProductReport: 0,
      // Employees
      viewEmployees: 0,
      addEmployees: 0,
      editEmployees: 0,
      deleteEmployees: 0,
    });

    setFormKey(prev => prev + 1);
    setIsModalOpen(true);
  };


  const openEditModal = (user) => {
    const safeNumber = (val) => Number(val ?? 0);

    const cleanedPermissions = {
      // Orders
      viewOrders: safeNumber(user.userData?.viewOrders ?? user.userData?.ViewOrders),
      // Products
      viewProducts: safeNumber(user.userData?.viewProducts ?? user.userData?.ViewProducts),
      createProduct: safeNumber(user.userData?.createProduct ?? user.userData?.CreateProduct),
      editProduct: safeNumber(user.userData?.editProduct ?? user.userData?.EditProduct),
      deleteProduct: safeNumber(user.userData?.deleteProduct ?? user.userData?.DeleteProduct),
      // Stock
      viewStock: safeNumber(user.userData?.viewStock ?? user.userData?.ViewStock),
      // Divisions
      viewDivisions: safeNumber(user.userData?.viewDivisions ?? user.userData?.ViewDivisions),
      addDivisions: safeNumber(user.userData?.addDivisions ?? user.userData?.AddDivisions),
      editDivisions: safeNumber(user.userData?.editDivisions ?? user.userData?.EditDivisions),
      deleteDivisions: safeNumber(user.userData?.deleteDivisions ?? user.userData?.DeleteDivisions),
      // Categories
      viewCategories: safeNumber(user.userData?.viewCategories ?? user.userData?.ViewCategories),
      addCategories: safeNumber(user.userData?.addCategories ?? user.userData?.AddCategories),
      editCategories: safeNumber(user.userData?.editCategories ?? user.userData?.EditCategories),
      deleteCategories: safeNumber(user.userData?.deleteCategories ?? user.userData?.DeleteCategories),
      // Customers
      viewCustomers: safeNumber(user.userData?.viewCustomers ?? user.userData?.ViewCustomers),
      addCustomers: safeNumber(user.userData?.addCustomers ?? user.userData?.AddCustomers),
      editCustomers: safeNumber(user.userData?.editCustomers ?? user.userData?.EditCustomers),
      deleteCustomers: safeNumber(user.userData?.deleteCustomers ?? user.userData?.DeleteCustomers),
      // Banners
      viewBanners: safeNumber(user.userData?.viewBanners ?? user.userData?.ViewBanners),
      addBanners: safeNumber(user.userData?.addBanners ?? user.userData?.AddBanners),
      editBanners: safeNumber(user.userData?.editBanners ?? user.userData?.EditBanners),
      deleteBanners: safeNumber(user.userData?.deleteBanners ?? user.userData?.DeleteBanners),
      // Theme
      viewTheme: safeNumber(user.userData?.viewTheme ?? user.userData?.ViewTheme),
      editTheme: safeNumber(user.userData?.editTheme ?? user.userData?.EditTheme),
      // Store Settings
      viewStoreSettings: safeNumber(user.userData?.viewStoreSettings ?? user.userData?.ViewStoreSettings),
      editStoreSettings: safeNumber(user.userData?.editStoreSettings ?? user.userData?.EditStoreSettings),
      // Coupons
      viewCoupons: safeNumber(user.userData?.viewCoupons ?? user.userData?.ViewCoupons),
      addCoupons: safeNumber(user.userData?.addCoupons ?? user.userData?.AddCoupons),
      editCoupons: safeNumber(user.userData?.editCoupons ?? user.userData?.EditCoupons),
      deleteCoupons: safeNumber(user.userData?.deleteCoupons ?? user.userData?.DeleteCoupons),
      // Activity
      viewActivity: safeNumber(user.userData?.viewActivity ?? user.userData?.ViewActivity),
      // Subscriptions
      viewSubscriptions: safeNumber(user.userData?.viewSubscriptions ?? user.userData?.ViewSubscriptions),
      editSubscriptions: safeNumber(user.userData?.editSubscriptions ?? user.userData?.EditSubscriptions),
      // Notification
      viewNotification: safeNumber(user.userData?.viewNotification ?? user.userData?.ViewNotification),
      addNotification: safeNumber(user.userData?.addNotification ?? user.userData?.AddNotification),
      // Payment Methods
      viewPaymentMethods: safeNumber(user.userData?.viewPaymentMethods ?? user.userData?.ViewPaymentMethods),
      addPaymentMethods: safeNumber(user.userData?.addPaymentMethods ?? user.userData?.AddPaymentMethods),
      editPaymentMethods: safeNumber(user.userData?.editPaymentMethods ?? user.userData?.EditPaymentMethods),
      deletePaymentMethods: safeNumber(user.userData?.deletePaymentMethods ?? user.userData?.DeletePaymentMethods),
      // Shipping
      viewShipping: safeNumber(user.userData?.viewShipping ?? user.userData?.ViewShipping),
      addShipping: safeNumber(user.userData?.addShipping ?? user.userData?.AddShipping),
      editShipping: safeNumber(user.userData?.editShipping ?? user.userData?.EditShipping),
      deleteShipping: safeNumber(user.userData?.deleteShipping ?? user.userData?.DeleteShipping),
      // Invoice
      viewInvoice: safeNumber(user.userData?.viewInvoice ?? user.userData?.ViewInvoice),
      editInvoice: safeNumber(user.userData?.editInvoice ?? user.userData?.EditInvoice),
      // Analytics
      viewAnalytics: safeNumber(user.userData?.viewAnalytics ?? user.userData?.ViewAnalytics),
      // Sales Report
      viewSalesReport: safeNumber(user.userData?.viewSalesReport ?? user.userData?.ViewSalesReport),
      // Inventory Report
      viewInventoryReport: safeNumber(user.userData?.viewInventoryReport ?? user.userData?.ViewInventoryReport),
      // Customer Report
      viewCustomerReport: safeNumber(user.userData?.viewCustomerReport ?? user.userData?.ViewCustomerReport),
      // Product Report
      viewProductReport: safeNumber(user.userData?.viewProductReport ?? user.userData?.ViewProductReport),
      // Employees
      viewEmployees: safeNumber(user.userData?.viewEmployees ?? user.userData?.ViewEmployees),
      addEmployees: safeNumber(user.userData?.addEmployees ?? user.userData?.AddEmployees),
      editEmployees: safeNumber(user.userData?.editEmployees ?? user.userData?.EditEmployees),
      deleteEmployees: safeNumber(user.userData?.deleteEmployees ?? user.userData?.DeleteEmployees),
    };

    setFormData({
      userId: user.id || 0,
      name: user.name || "",
      engName: user.engName || "",
      email: user.email || "",
      password: "",
      isActive: user.active ? 1 : 0,
      userTypeID: user.userTypeID || 1,
      userFunctionID: user.userFunctionID || 1,
      ...cleanedPermissions,
    });

    setFormKey(prev => prev + 1);
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.engName.trim()) {
      Swal.fire({ icon: "warning", text: isArabic ? "الاسم مطلوب" : "Name is required", customClass: { container: 'swal-above-modal' } });
      return;
    }
    if (!formData.email.includes("@")) {
      Swal.fire({ icon: "warning", text: isArabic ? "بريد إلكتروني غير صحيح" : "Invalid email", customClass: { container: 'swal-above-modal' } });
      return;
    }
    if (formData.userId === 0 && !formData.password) {
      Swal.fire({ icon: "warning", text: isArabic ? "كلمة المرور مطلوبة للموظف الجديد" : "Password required", customClass: { container: 'swal-above-modal' } });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("authToken");

      const isEdit = formData.userId !== 0;

      if (isEdit) {

        const payload = {
          userID: formData.userId,
          username: formData.name.trim(),
          engUsername: formData.engName.trim(),
          email: formData.email.trim(),
          tenantId: tenantId,
          isActive: formData.isActive,
        };

        if (formData.password?.trim()) {
          payload.password = formData.password.trim();
        }


        if (!formData.password?.trim()) {
          delete payload.password;
        }

        // Remove password if empty (for edit mode when not changing password)
        if (!payload.password) {
          delete payload.password;
        }

        console.log("📤 Sending EditTenantUsers request:");
        console.log("📤 Endpoint:", `${baseUrl}/EditTenantUsers?TenantId=${tenantId}`);
        console.log("📤 Query TenantId:", tenantId);
        console.log("📤 Payload userID:", payload.userID);
        console.log("📤 Payload username:", payload.username);
        console.log("📤 Payload tenantId:", payload.tenantId);
        console.log("📤 Full payload:", payload);
        if (!payload.password || payload.password.trim() === "") {
          delete payload.password;
        }

        const res = await fetch(`${baseUrl}/EditTenantUsers?TenantId=${tenantId}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "Authorization": token ? `Bearer ${token}` : "",
          },
          body: JSON.stringify(payload),
        });

        let result = {};
        const contentType = res.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          try {
            result = await res.json();
          } catch (parseError) {
            const text = await res.text();
            throw new Error(text || "Invalid response format");
          }
        } else {
          const text = await res.text();
          if (text) {
            try {
              result = JSON.parse(text);
            } catch {
              result = { message: text || `HTTP ${res.status}: ${res.statusText}` };
            }
          }
        }

        if (!res.ok) {
          const errorMessage = result?.message || result?.error || result?.Message || "Failed to update";
          throw new Error(errorMessage);
        }

        // Close modal immediately
        setIsModalOpen(false);

        // Refresh employees list immediately to show updated employee
        console.log("🔄 Refreshing employees list after updating employee...");
        await fetchEmployees();
        console.log("✅ Employees list refreshed, updated employee should be visible now");

        Swal.fire({
          icon: "success",
          title: isArabic ? "تم التحديث! ✅" : "Updated! ✅",
          timer: 1500,
          showConfirmButton: false,
          confirmButtonColor: colors.mainColor,
          background: isDark ? "#1E293B" : "#fff",
          color: isDark ? "#fff" : "#000",
          customClass: { container: 'swal-above-modal' },
        });
      } else {
        // Add mode - use AddTenantUsers
        const generateUniqueId = () => {
          // Generate a unique ID that is always > 0 and within int32 range
          const timestamp = Date.now();
          const last5Digits = String(timestamp).slice(-5);
          const random3Digits = Math.floor(Math.random() * 1000);
          let uniqueId = parseInt(last5Digits + String(random3Digits).padStart(3, '0'));

          // Ensure the ID is always > 0 and within int32 range (max: 2147483647)
          if (uniqueId <= 0) {
            uniqueId = Math.abs(uniqueId) + 1;
          }
          if (uniqueId > 2147483647) {
            // If too large, take modulo and add minimum value
            uniqueId = (uniqueId % 2147483647) + 1;
          }

          return uniqueId;
        };

        const uniqueUserId = generateUniqueId();

        // Validate that userID is not 0 before sending
        if (!uniqueUserId || uniqueUserId === 0) {
          throw new Error(isArabic
            ? "خطأ في إنشاء معرف المستخدم. يرجى المحاولة مرة أخرى"
            : "Error generating user ID. Please try again");
        }

        console.log("🆔 Generated UserID:", uniqueUserId, "(should be > 0)");

        // Format date as "Nov 18 2025  3:55PM" (matching API example)
        const formatDate = (date) => {
          const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
          const month = months[date.getMonth()];
          const day = date.getDate();
          const year = date.getFullYear();
          const hours = date.getHours();
          const minutes = date.getMinutes();
          const ampm = hours >= 12 ? "PM" : "AM";
          const hour12 = hours % 12 || 12;
          const minStr = String(minutes).padStart(2, '0');
          return `${month} ${day} ${year}  ${hour12}:${minStr}${ampm}`;
        };

        // API expects camelCase for all fields
        const payload = {
          userID: uniqueUserId,
          userTypeID: formData.userTypeID || 1,
          userFunctionID: 1,
          username: formData.name.trim(),
          password: formData.password.trim(),
          tenantId: tenantId,
          email: formData.email.trim(),
          isActive: formData.isActive !== undefined ? formData.isActive : 1,
          isDeleted: 0,
          engUsername: formData.engName.trim(),
          // Permissions
          // Orders
          viewOrders: formData.viewOrders || 0,
          // Products
          viewProducts: formData.viewProducts || 0,
          createProduct: formData.createProduct || 0,
          editProduct: formData.editProduct || 0,
          deleteProduct: formData.deleteProduct || 0,
          // Stock
          viewStock: formData.viewStock || 0,
          // Divisions
          viewDivisions: formData.viewDivisions || 0,
          addDivisions: formData.addDivisions || 0,
          editDivisions: formData.editDivisions || 0,
          deleteDivisions: formData.deleteDivisions || 0,
          // Categories
          viewCategories: formData.viewCategories || 0,
          addCategories: formData.addCategories || 0,
          editCategories: formData.editCategories || 0,
          deleteCategories: formData.deleteCategories || 0,
          // Customers
          viewCustomers: formData.viewCustomers || 0,
          addCustomers: formData.addCustomers || 0,
          editCustomers: formData.editCustomers || 0,
          deleteCustomers: formData.deleteCustomers || 0,
          // Banners
          viewBanners: formData.viewBanners || 0,
          addBanners: formData.addBanners || 0,
          editBanners: formData.editBanners || 0,
          deleteBanners: formData.deleteBanners || 0,
          // Theme
          viewTheme: formData.viewTheme || 0,
          editTheme: formData.editTheme || 0,
          // Store Settings
          viewStoreSettings: formData.viewStoreSettings || 0,
          editStoreSettings: formData.editStoreSettings || 0,
          // Coupons
          viewCoupons: formData.viewCoupons || 0,
          addCoupons: formData.addCoupons || 0,
          editCoupons: formData.editCoupons || 0,
          deleteCoupons: formData.deleteCoupons || 0,
          // Activity
          viewActivity: formData.viewActivity || 0,
          // Subscriptions
          viewSubscriptions: formData.viewSubscriptions || 0,
          editSubscriptions: formData.editSubscriptions || 0,
          // Notification
          viewNotification: formData.viewNotification || 0,
          addNotification: formData.addNotification || 0,
          // Payment Methods
          viewPaymentMethods: formData.viewPaymentMethods || 0,
          addPaymentMethods: formData.addPaymentMethods || 0,
          editPaymentMethods: formData.editPaymentMethods || 0,
          deletePaymentMethods: formData.deletePaymentMethods || 0,
          // Shipping
          viewShipping: formData.viewShipping || 0,
          addShipping: formData.addShipping || 0,
          editShipping: formData.editShipping || 0,
          deleteShipping: formData.deleteShipping || 0,
          // Invoice
          viewInvoice: formData.viewInvoice || 0,
          editInvoice: formData.editInvoice || 0,
          // Analytics
          viewAnalytics: formData.viewAnalytics || 0,
          // Sales Report
          viewSalesReport: formData.viewSalesReport || 0,
          // Inventory Report
          viewInventoryReport: formData.viewInventoryReport || 0,
          // Customer Report
          viewCustomerReport: formData.viewCustomerReport || 0,
          // Product Report
          viewProductReport: formData.viewProductReport || 0,
          // Employees
          viewEmployees: formData.viewEmployees || 0,
          addEmployees: formData.addEmployees || 0,
          editEmployees: formData.editEmployees || 0,
          deleteEmployees: formData.deleteEmployees || 0,
        };

        console.log("📤 Sending AddTenantUsers request:");
        console.log("📤 Endpoint:", `${baseUrl}/AddTenantUsers?TenantId=${tenantId}`);
        console.log("📤 Query TenantId:", tenantId);
        console.log("📤 Payload userID:", payload.userID, "(must be > 0)");
        console.log("📤 Payload username:", payload.username);
        console.log("📤 Payload tenantId:", payload.tenantId);
        console.log("📤 Full payload:", payload);

        const res = await fetch(`${baseUrl}/AddTenantUsers?TenantId=${tenantId}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "Authorization": token ? `Bearer ${token}` : "",
          },
          body: JSON.stringify(payload),
        });

        let result = {};
        const contentType = res.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          try {
            result = await res.json();
          } catch (parseError) {
            const text = await res.text();
            throw new Error(text || "Invalid response format");
          }
        } else {
          const text = await res.text();
          if (text) {
            try {
              result = JSON.parse(text);
            } catch {
              result = { message: text || `HTTP ${res.status}: ${res.statusText}` };
            }
          }
        }

        console.log("📥 AddTenantUsers response status:", res.status, res.statusText);
        console.log("📥 AddTenantUsers response data:", result);

        if (!res.ok) {
          const errorMessage = result?.message || result?.error || result?.Message || "Failed to add";
          console.error("❌ AddTenantUsers failed:", errorMessage);
          console.error("❌ Full error response:", result);

          // Check for validation errors
          if (result?.errors && typeof result.errors === 'object') {
            const validationErrors = Object.entries(result.errors)
              .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
              .join('\n');
            console.error("❌ Validation errors:", validationErrors);
            throw new Error(isArabic
              ? `خطأ في التحقق من البيانات:\n${validationErrors}`
              : `Validation error:\n${validationErrors}`);
          }

          throw new Error(errorMessage);
        }

        console.log("✅ AddTenantUsers successful:", result?.message || "Employee added successfully");

        // Close modal immediately
        setIsModalOpen(false);

        // Refresh employees list immediately to show new employee in table
        console.log("🔄 Refreshing employees list after adding new employee...");
        await fetchEmployees();
        console.log("✅ Employees list refreshed, new employee should be visible now");

        Swal.fire({
          icon: "success",
          title: isArabic ? "تم الإضافة! ✅" : "Added! ✅",
          text: isArabic ? "تم إضافة الموظف بنجاح وظهر في القائمة" : "Employee added successfully and shown in the list",
          timer: 2000,
          showConfirmButton: false,
          confirmButtonColor: colors.mainColor,
          background: isDark ? "#1E293B" : "#fff",
          color: isDark ? "#fff" : "#000",
          customClass: { container: 'swal-above-modal' },
        });
      }
    } catch (err) {
      console.error("Save error:", err);
      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل الحفظ" : "Save failed",
        text: err.message,
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
        customClass: { container: 'swal-above-modal' },
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const emp = employees.find((e) => e.id === id);
    const res = await Swal.fire({
      icon: "warning",
      title: isArabic ? "حذف الموظف نهائياً؟" : "Delete employee permanently?",
      html: isArabic
        ? `<p>سيتم حذف <strong>${emp?.name}</strong> نهائياً من النظام</p><p style="color: #ef4444; margin-top: 10px; font-size: 0.9em;">⚠️ هذا الإجراء لا يمكن التراجع عنه!</p>`
        : `<p>This will permanently delete <strong>${emp?.name}</strong> from the system</p><p style="color: #ef4444; margin-top: 10px; font-size: 0.9em;">⚠️ This action cannot be undone!</p>`,
      showCancelButton: true,
      confirmButtonColor: colors.red,
      cancelButtonColor: "#6b7280",
      confirmButtonText: isArabic ? "نعم، حذف نهائياً" : "Yes, delete permanently",
      cancelButtonText: isArabic ? "إلغاء" : "Cancel",
      background: isDark ? "#1E293B" : "#fff",
      color: isDark ? "#fff" : "#000",
      customClass: { container: 'swal-above-modal' },
    });

    if (res.isConfirmed) {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");

        console.log("🗑️ Deleting employee permanently:", { UserID: id, TenantId: tenantId });
        const deleteRes = await fetch(`${baseUrl}/TenantUser?UserID=${id}&TenantId=${tenantId}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "Authorization": token ? `Bearer ${token}` : "",
          },
        });

        let resultData = {};
        const contentType = deleteRes.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          try {
            resultData = await deleteRes.json();
          } catch (parseError) {
            const text = await deleteRes.text();
            console.error("Failed to parse JSON response:", parseError);
            console.error("Response text:", text);
          }
        } else {
          const text = await deleteRes.text();
          if (text) {
            try {
              resultData = JSON.parse(text);
            } catch {
              resultData = { message: text || `HTTP ${deleteRes.status}: ${deleteRes.statusText}` };
            }
          }
        }

        console.log("📥 DeleteTenantUser response status:", deleteRes.status, deleteRes.statusText);
        console.log("📥 DeleteTenantUser response data:", resultData);

        if (deleteRes.ok) {
          // Refresh employees list after successful delete
          await fetchEmployees();
          Swal.fire({
            icon: "success",
            title: isArabic ? "تم الحذف! ✅" : "Deleted! ✅",
            text: resultData?.message || (isArabic ? "تم حذف الموظف بنجاح" : "Employee deleted successfully"),
            timer: 1500,
            showConfirmButton: false,
            confirmButtonColor: colors.mainColor,
            background: isDark ? "#1E293B" : "#fff",
            color: isDark ? "#fff" : "#000",
            customClass: { container: 'swal-above-modal' },
          });
        } else {
          const errorMessage = resultData?.message || resultData?.error || resultData?.Message || "Delete failed";
          console.error("❌ DeleteTenantUser failed:", errorMessage);
          throw new Error(errorMessage);
        }
      } catch (err) {
        console.error("Delete error:", err);
        Swal.fire({
          icon: "error",
          title: isArabic ? "فشل الحذف ❌" : "Delete Failed ❌",
          text: err.message,
          confirmButtonColor: colors.mainColor,
          background: isDark ? "#1E293B" : "#fff",
          color: isDark ? "#fff" : "#000",
          customClass: { container: 'swal-above-modal' },
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleToggleActive = async (id) => {
    const emp = employees.find((e) => e.id === id);
    const newStatus = !emp.active;

    const res = await Swal.fire({
      icon: "question",
      title: isArabic ? "تغيير حالة الموظف؟" : "Change employee status?",
      text: isArabic
        ? `سيتم ${newStatus ? "تفعيل" : "تعطيل"} حساب ${emp?.name}`
        : `This will ${newStatus ? "activate" : "deactivate"} ${emp?.name}'s account`,
      showCancelButton: true,
      confirmButtonColor: newStatus ? colors.mainColor : colors.red,
      cancelButtonColor: "#6b7280",
      confirmButtonText: isArabic ? "نعم، تغيير" : "Yes, change",
      cancelButtonText: isArabic ? "إلغاء" : "Cancel",
      background: isDark ? "#1E293B" : "#fff",
      color: isDark ? "#fff" : "#000",
      customClass: { container: "swal-above-modal" },
    });

    if (!res.isConfirmed) return;

    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      const payload = {
        userID: emp.id,
        userTypeID: emp.userTypeID || 1,
        userFunctionID: emp.userFunctionID || 1,
        username: emp.name,
        engUsername: emp.engName,
        email: emp.email,
        tenantId: tenantId,
        isActive: newStatus ? 1 : 0,
        isDeleted: 0,
      };

      const updateRes = await fetch(
        `${baseUrl}/EditTenantUsers?TenantId=${tenantId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
          body: JSON.stringify(payload),
        }
      );

      const resultData = await updateRes.json();

      if (!updateRes.ok) {
        const errorMessage =
          resultData?.message || resultData?.error || "Update failed";
        throw new Error(errorMessage);
      }

      await fetchEmployees();

      Swal.fire({
        icon: "success",
        title: isArabic ? "تم التحديث ✅" : "Updated ✅",
        timer: 1500,
        showConfirmButton: false,
        customClass: { container: "swal-above-modal" },
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل التحديث ❌" : "Update Failed ❌",
        text: err.message,
        customClass: { container: "swal-above-modal" },
      });
    } finally {
      setLoading(false);
    }
  };



  const togglePermission = (key) => {
    setFormData(prev => {
      const updated = { ...prev };
      updated[key] = prev[key] === 1 ? 0 : 1;

      // إذا عطّل صلاحية رئيسية (view)، عطّل جميع الصلاحيات التابعة
      if (updated[key] === 0) {
        let relatedPermissions = [];

        // تحديد الصلاحيات التابعة بناءً على الصلاحية الرئيسية
        if (key === "viewProducts") {
          relatedPermissions = ["viewProducts", "createProduct", "editProduct", "deleteProduct"];
        } else if (key === "viewDivisions") {
          relatedPermissions = ["viewDivisions", "addDivisions", "editDivisions", "deleteDivisions"];
        } else if (key === "viewCategories") {
          relatedPermissions = ["viewCategories", "addCategories", "editCategories", "deleteCategories"];
        } else if (key === "viewCustomers") {
          relatedPermissions = ["viewCustomers", "addCustomers", "editCustomers", "deleteCustomers"];
        } else if (key === "viewBanners") {
          relatedPermissions = ["viewBanners", "addBanners", "editBanners", "deleteBanners"];
        } else if (key === "viewTheme") {
          relatedPermissions = ["viewTheme", "editTheme"];
        } else if (key === "viewStoreSettings") {
          relatedPermissions = ["viewStoreSettings", "editStoreSettings"];
        } else if (key === "viewCoupons") {
          relatedPermissions = ["viewCoupons", "addCoupons", "editCoupons", "deleteCoupons"];
        } else if (key === "viewSubscriptions") {
          relatedPermissions = ["viewSubscriptions", "editSubscriptions"];
        } else if (key === "viewNotification") {
          relatedPermissions = ["viewNotification", "addNotification"];
        } else if (key === "viewPaymentMethods") {
          relatedPermissions = ["viewPaymentMethods", "addPaymentMethods", "editPaymentMethods", "deletePaymentMethods"];
        } else if (key === "viewShipping") {
          relatedPermissions = ["viewShipping", "addShipping", "editShipping", "deleteShipping"];
        } else if (key === "viewInvoice") {
          relatedPermissions = ["viewInvoice", "editInvoice"];
        } else if (key === "viewEmployees") {
          relatedPermissions = ["viewEmployees", "addEmployees", "editEmployees", "deleteEmployees"];
        }

        // تعطيل جميع الصلاحيات التابعة
        if (relatedPermissions.length > 0) {
          relatedPermissions.forEach(perm => {
            updated[perm] = 0;
          });

          // إظهار Toast توضيحي
          Swal.fire({
            icon: "warning",
            title: isArabic ? "تنبيه" : "Notice",
            text: isArabic
              ? "بما أنك عطلت الصلاحية الأساسية، جميع صلاحيات العمليات التابعة تم تعطيلها"
              : "Since you disabled the main view permission, all related CRUD permissions are disabled.",
            timer: 4500,
            showConfirmButton: false,
            toast: true,
            position: "top-end",
            background: isDark ? "#1E293B" : "#fff",
            color: isDark ? "#fff" : "#000",
            customClass: { container: 'swal-above-modal' },
          });
        }
      }

      return updated;
    });
  };

  const toggleSection = (keys, value) => {
    setFormData(prev => {
      const updated = { ...prev };
      keys.forEach(k => updated[k] = value ? 1 : 0);
      return updated;
    });
  };

  const toggleAccordion = (key) => {
    setOpenAccordion(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getName = (emp) => isArabic ? (emp.name || "—") : (emp.engName || emp.name || "—");

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#7400BF]"></div>
          <p className="mt-4 text-gray-500">{isArabic ? "جاري التحميل..." : "Loading..."}</p>
        </div>
      </div>
    );
  }

  return (
    <div dir={isArabic ? "rtl" : "ltr"} className="w-full">

      {/* Table */}
      <div className="w-full">
        <ResponsiveTable
          columns={[
            { header: isArabic ? "الاسم" : "Name", sortKey: "name" },
            { header: isArabic ? "البريد الإلكتروني" : "Email", sortKey: "email" },
            { header: isArabic ? "الحالة" : "Status", sortKey: "active" },
            { header: isArabic ? "الإجراءات" : "Actions" },
          ]}
          data={employees}
          searchable
          sortable
          headerActions={
            <motion.button
              onClick={openAddModal}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-semibold shadow-md"
              style={{ background: colors.mainColor }}
            >
              <FiUserPlus size={18} />
              {isArabic ? "إضافة موظف" : "Add Employee"}
            </motion.button>
          }
          renderRow={(emp) => (
            <>
              {/* Name */}
              <td className="py-2 px-4 align-middle leading-tight">
                <div className="flex flex-col items-start gap-0.5">
                  <span
                    className="font-semibold"
                    style={{
                      color: isDark ? "#F1F5F9" : "#1E293B",
                      fontSize: "0.95rem",
                    }}
                  >
                    {getName(emp)}
                  </span>

                  {emp.engName && emp.engName !== getName(emp) && (
                    <span
                      className="text-xs opacity-70"
                      style={{
                        color: isDark ? "#94A3B8" : "#64748B",
                      }}
                    >
                      {emp.engName}
                    </span>
                  )}
                </div>
              </td>

              {/* Email */}
              <td className="py-2 px-4 align-middle leading-tight">
                <div className="flex flex-col items-start gap-0.5">
                  <span
                    className="font-medium"
                    style={{
                      color: emp.email
                        ? isDark
                          ? "#F1F5F9"
                          : "#1E293B"
                        : isDark
                          ? "#94A3B8"
                          : "#64748B",
                      fontSize: "0.9rem",
                      fontStyle: emp.email ? "normal" : "italic",
                    }}
                  >
                    {emp.email || (isArabic ? "غير متوفر" : "Not available")}
                  </span>
                </div>
              </td>

              {/* Status */}
              <td className="py-2 px-4 align-middle leading-tight">
                <div className="flex flex-col items-start gap-0.5">

                  <span
                    className="px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
                    style={{
                      backgroundColor: emp.active
                        ? isDark
                          ? "rgba(34, 197, 94, 0.15)"
                          : "rgba(34, 197, 94, 0.1)"
                        : isDark
                          ? "rgba(239, 68, 68, 0.15)"
                          : "rgba(239, 68, 68, 0.1)",
                      color: emp.active
                        ? isDark
                          ? "#4ADE80"
                          : "#16A34A"
                        : isDark
                          ? "#F87171"
                          : "#DC2626",
                      border: `1px solid ${emp.active
                        ? isDark
                          ? "rgba(34, 197, 94, 0.3)"
                          : "rgba(34, 197, 94, 0.2)"
                        : isDark
                          ? "rgba(239, 68, 68, 0.3)"
                          : "rgba(239, 68, 68, 0.2)"
                        }`,
                    }}
                  >
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{
                        backgroundColor: emp.active
                          ? isDark
                            ? "#4ADE80"
                            : "#16A34A"
                          : isDark
                            ? "#F87171"
                            : "#DC2626",
                      }}
                    />
                    {emp.active
                      ? isArabic
                        ? "نشط"
                        : "Active"
                      : isArabic
                        ? "معطل"
                        : "Inactive"}
                  </span>
                </div>
              </td>

              {/* Actions */}
              <td className="py-2 px-4 align-middle leading-tight">
                <div className="flex flex-row items-center gap-2 whitespace-nowrap">
                  <motion.button
                    onClick={() => openEditModal(emp)}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="p-2.5 rounded-lg text-white shadow-sm "
                    style={{ background: colors.gold }}
                    title={isArabic ? "تعديل" : "Edit"}
                  >
                    <FiEdit2 size={16} />
                  </motion.button>

                  <motion.button
                    onClick={() => handleToggleActive(emp.id)}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="p-2.5 rounded-lg text-white shadow-sm "
                    style={{ background: emp.active ? colors.red : colors.mainColor }}
                    title={
                      emp.active
                        ? isArabic
                          ? "تعطيل"
                          : "Deactivate"
                        : isArabic
                          ? "تفعيل"
                          : "Activate"
                    }
                  >
                    {emp.active ? <FiUserX size={16} /> : <FiUserCheck size={16} />}
                  </motion.button>

                  <motion.button
                    onClick={() => handleDelete(emp.id)}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="p-2.5 rounded-lg text-white shadow-sm "
                    style={{ background: "#991b1b" }}
                    title={isArabic ? "حذف نهائي" : "Delete"}
                  >
                    <FiTrash2 size={16} />
                  </motion.button>
                </div>
              </td>
            </>
          )}
        />
      </div>

      {/* Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            key={`overlay-${formKey}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              key={`modal-${formKey}`}
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className={`relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${isDark ? "bg-[#0F172A] text-gray-100" : "bg-white text-gray-900"
                }`}
              style={{
                border: isDark ? `1px solid rgba(255, 255, 255, 0.1)` : "1px solid #e5e7eb",
              }}
            >
              {/* Modal Header */}
              <div
                className="sticky top-0 z-10 flex items-center justify-between p-6 border-b"
                style={{
                  borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "#e5e7eb",
                  backgroundColor: isDark ? "rgba(15, 23, 42, 0.95)" : "#ffffff",
                  backdropFilter: isDark ? "blur(10px)" : "none"
                }}
              >
                <h2 className="text-xl font-bold" style={{ color: colors.mainColor }}>
                  {formData.userId === 0
                    ? (isArabic ? "إضافة موظف جديد" : "Add New Employee")
                    : (isArabic ? "تعديل موظف" : "Edit Employee")
                  }
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  style={{
                    // color: isDark ? "#94a3b8" : "#6b7280",
                    background: isDark ? "rgba(239, 68, 68, 0.2)" : "#fee2e2",
                    color: "#ef4444"
                  }}
                  className="transition p-2 rounded-lg "
                >
                  <FiX size={20} />
                </button>
              </div>

              {/* Modal Body */}
              <div
                className="p-6 space-y-5"
                style={{
                  backgroundColor: isDark ? "#0F172A" : "#ffffff"
                }}
              >
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-2 font-semibold" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                      {isArabic ? "الاسم (عربي)" : "Name (Arabic)"}
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg text-sm transition-all focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: isDark ? "#1E2533" : "#ffffff",
                        border: `1px solid ${isDark ? "rgba(255,255,255,0.2)" : "#d1d5db"}`,
                        color: isDark ? "#fff" : "#111827",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = colors.mainColor;
                        e.target.style.boxShadow = `0 0 0 2px ${colors.mainColor}20`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = isDark ? "rgba(255,255,255,0.2)" : "#d1d5db";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                  <div>
                    <label className="block mb-2 font-semibold" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                      {isArabic ? "الاسم (إنجليزي)" : "Name (English)"}
                    </label>
                    <input
                      type="text"
                      value={formData.engName}
                      onChange={(e) => setFormData({ ...formData, engName: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg text-sm transition-all focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: isDark ? "#1E2533" : "#ffffff",
                        border: `1px solid ${isDark ? "rgba(255,255,255,0.2)" : "#d1d5db"}`,
                        color: isDark ? "#fff" : "#111827",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = colors.mainColor;
                        e.target.style.boxShadow = `0 0 0 2px ${colors.mainColor}20`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = isDark ? "rgba(255,255,255,0.2)" : "#d1d5db";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                  <div>
                    <label className="block mb-2 font-semibold" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                      Email
                    </label>
                    <input
                      key={`email-${formKey}`}
                      type="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg text-sm transition-all focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: isDark ? "#1E2533" : "#ffffff",
                        border: `1px solid ${isDark ? "rgba(255,255,255,0.2)" : "#d1d5db"}`,
                        color: isDark ? "#fff" : "#111827",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = colors.mainColor;
                        e.target.style.boxShadow = `0 0 0 2px ${colors.mainColor}20`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = isDark ? "rgba(255,255,255,0.2)" : "#d1d5db";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                  <div>
                    <label className="block mb-2 font-semibold" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                      {formData.userId === 0
                        ? (isArabic ? "كلمة المرور" : "Password")
                        : (isArabic ? "كلمة مرور جديدة (اختياري)" : "New Password (optional)")
                      }
                    </label>
                    <input
                      key={`password-${formKey}`}
                      type="password"
                      autoComplete="new-password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg text-sm transition-all focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: isDark ? "#1E2533" : "#ffffff",
                        border: `1px solid ${isDark ? "rgba(255,255,255,0.2)" : "#d1d5db"}`,
                        color: isDark ? "#fff" : "#111827",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = colors.mainColor;
                        e.target.style.boxShadow = `0 0 0 2px ${colors.mainColor}20`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = isDark ? "rgba(255,255,255,0.2)" : "#d1d5db";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                </div>

                {/* Active Switch - Same design as Divisions.jsx */}
                <div className="mb-4" dir={isArabic ? "rtl" : "ltr"}>
                  <label className="block mb-3 font-semibold" style={{ color: isDark ? "#f1f5f9" : "#111827", textAlign: isArabic ? "right" : "left" }}>
                    {isArabic ? "الحالة" : "Status"}
                  </label>
                  <div
                    className="flex items-center"
                    style={{
                      justifyContent: "flex-start",
                      direction: isArabic ? "rtl" : "ltr"
                    }}
                  >
                    <label
                      className="flex items-center gap-3 cursor-pointer group"
                      style={{
                        flexDirection: isArabic ? "row" : "row",
                        direction: isArabic ? "rtl" : "ltr"
                      }}
                    >
                      {/* Toggle Switch - Same design as Divisions.jsx */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setFormData({ ...formData, isActive: formData.isActive === 1 ? 0 : 1 });
                        }}
                        className="relative w-12 h-6 rounded-full transition-colors cursor-pointer"
                        style={{
                          backgroundColor: formData.isActive === 1 ? colors.mainColor : (isDark ? "#475569" : "#d1d5db")
                        }}
                      >
                        <span
                          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${formData.isActive === 1 ? "translate-x-6" : "translate-x-0"
                            }`}
                        />
                      </button>

                      {/* Status Badge - Same design as Divisions.jsx */}
                      <div
                        className={`
                          px-4 py-1.5 rounded-lg font-semibold text-sm transition-all duration-300
                          ${formData.isActive === 1
                            ? 'bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20'
                            : 'bg-gray-500/10 text-gray-600 dark:text-gray-400 border border-gray-500/20'
                          }
                        `}
                        style={{
                          backgroundColor: formData.isActive === 1
                            ? 'rgba(16, 185, 129, 0.1)'
                            : (isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(107, 114, 128, 0.1)'),
                          color: formData.isActive === 1
                            ? '#059669'
                            : (isDark ? '#94a3b8' : '#4b5563'),
                          borderColor: formData.isActive === 1
                            ? 'rgba(16, 185, 129, 0.2)'
                            : (isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(107, 114, 128, 0.2)'),
                        }}
                      >
                        {formData.isActive === 1
                          ? (isArabic ? "✓ نشط" : "✓ Active")
                          : (isArabic ? "✗ غير نشط" : "✗ Inactive")
                        }
                      </div>
                    </label>
                  </div>
                </div>

                {/* Permissions Section */}
                <div>
                  <h3
                    className="text-lg font-bold mb-4"
                    style={{ color: isDark ? "#f1f5f9" : "#111827" }}
                  >
                    {isArabic ? "الصلاحيات" : "Permissions"}
                  </h3>

                  {/* Accordion Items */}
                  {[
                    { key: "orders", label: isArabic ? "الطلبات" : "Orders", permissions: ["viewOrders"], mainPermission: null },
                    { key: "products", label: isArabic ? "المنتجات" : "Products", permissions: ["viewProducts", "createProduct", "editProduct", "deleteProduct"], mainPermission: "viewProducts" },
                    { key: "stock", label: isArabic ? "المخزون" : "Stock", permissions: ["viewStock"], mainPermission: null },
                    { key: "divisions", label: isArabic ? "الأقسام" : "Divisions", permissions: ["viewDivisions", "addDivisions", "editDivisions", "deleteDivisions"], mainPermission: "viewDivisions" },
                    { key: "categories", label: isArabic ? "البراندات" : "Categories", permissions: ["viewCategories", "addCategories", "editCategories", "deleteCategories"], mainPermission: "viewCategories" },
                    { key: "customers", label: isArabic ? "الزبائن" : "Customers", permissions: ["viewCustomers", "addCustomers", "editCustomers", "deleteCustomers"], mainPermission: "viewCustomers" },
                    { key: "banners", label: isArabic ? "البانرات" : "Banners", permissions: ["viewBanners", "addBanners", "editBanners", "deleteBanners"], mainPermission: "viewBanners" },
                    { key: "theme", label: isArabic ? "الثيم" : "Theme", permissions: ["viewTheme", "editTheme"], mainPermission: "viewTheme" },
                    { key: "storeSettings", label: isArabic ? "إعدادات المتجر" : "Store Settings", permissions: ["viewStoreSettings", "editStoreSettings"], mainPermission: "viewStoreSettings" },
                    { key: "coupons", label: isArabic ? "الكوبونات" : "Coupons", permissions: ["viewCoupons", "addCoupons", "editCoupons", "deleteCoupons"], mainPermission: "viewCoupons" },
                    { key: "activity", label: isArabic ? "سجل الإجراءات" : "Activity", permissions: ["viewActivity"], mainPermission: null },
                    { key: "subscriptions", label: isArabic ? "الاشتراكات" : "Subscriptions", permissions: ["viewSubscriptions", "editSubscriptions"], mainPermission: "viewSubscriptions" },
                    { key: "notification", label: isArabic ? "الإشعارات" : "Notifications", permissions: ["viewNotification", "addNotification"], mainPermission: "viewNotification" },
                    { key: "paymentMethods", label: isArabic ? "طرق الدفع" : "Payment Methods", permissions: ["viewPaymentMethods", "addPaymentMethods", "editPaymentMethods", "deletePaymentMethods"], mainPermission: "viewPaymentMethods" },
                    { key: "shipping", label: isArabic ? "الشحن" : "Shipping", permissions: ["viewShipping", "addShipping", "editShipping", "deleteShipping"], mainPermission: "viewShipping" },
                    { key: "invoice", label: isArabic ? "الفاتورة" : "Invoice", permissions: ["viewInvoice", "editInvoice"], mainPermission: "viewInvoice" },
                    { key: "analytics", label: isArabic ? "التحليلات" : "Analytics", permissions: ["viewAnalytics"], mainPermission: null },
                    { key: "salesReport", label: isArabic ? "تقرير المبيعات" : "Sales Report", permissions: ["viewSalesReport"], mainPermission: null },
                    { key: "inventoryReport", label: isArabic ? "تقرير المخزون" : "Inventory Report", permissions: ["viewInventoryReport"], mainPermission: null },
                    { key: "customerReport", label: isArabic ? "تقرير العملاء" : "Customer Report", permissions: ["viewCustomerReport"], mainPermission: null },
                    { key: "productReport", label: isArabic ? "تقرير المنتجات" : "Product Report", permissions: ["viewProductReport"], mainPermission: null },
                    { key: "employees", label: isArabic ? "الموظفين" : "Employees", permissions: ["viewEmployees", "addEmployees", "editEmployees", "deleteEmployees"], mainPermission: "viewEmployees" },
                  ].map((section) => {
                    const allChecked = section.permissions.every(p => formData[p] === 1);
                    return (
                      <div
                        key={section.key}
                        className="mb-2 border rounded-lg overflow-hidden"
                        style={{
                          borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "#E5E7EB"
                        }}
                      >
                        <div
                          onClick={() => toggleAccordion(section.key)}
                          className="w-full flex items-center justify-between p-4 transition cursor-pointer"
                          style={{
                            backgroundColor: isDark ? "#1E293B" : "#f9fafb"
                          }}
                        >
                          <span
                            className="font-semibold"
                            style={{ color: isDark ? "#f1f5f9" : "#111827" }}
                          >
                            {section.label}
                          </span>
                          <div className="flex items-center gap-3">
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSection(section.permissions, !allChecked);
                              }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleSection(section.permissions, !allChecked);
                                }
                              }}
                              className="relative w-10 h-5 rounded-full transition-colors cursor-pointer"
                              style={{
                                backgroundColor: allChecked ? colors.mainColor : (isDark ? "#475569" : "#d1d5db")
                              }}
                            >
                              <span
                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${allChecked ? "translate-x-5" : "translate-x-0"
                                  }`}
                              />
                            </div>
                            {openAccordion[section.key] ? <FiChevronUp /> : <FiChevronDown />}
                          </div>
                        </div>
                        {openAccordion[section.key] && (
                          <div
                            className="p-4 space-y-3 border-t"
                            style={{
                              borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "#E5E7EB",
                              backgroundColor: isDark ? "rgba(30, 41, 59, 0.5)" : "#FAFBFC"
                            }}
                          >
                            {section.permissions.map((perm) => {
                              const permLabels = {
                                // Orders
                                viewOrders: isArabic ? "عرض الطلبات" : "View Orders",
                                // Products
                                viewProducts: isArabic ? "عرض المنتجات" : "View Products",
                                createProduct: isArabic ? "إنشاء منتج" : "Create Product",
                                editProduct: isArabic ? "تعديل منتج" : "Edit Product",
                                deleteProduct: isArabic ? "حذف منتج" : "Delete Product",
                                // Stock
                                viewStock: isArabic ? "عرض المخزون" : "View Stock",
                                // Divisions
                                viewDivisions: isArabic ? "عرض الأقسام" : "View Divisions",
                                addDivisions: isArabic ? "إضافة قسم" : "Add Division",
                                editDivisions: isArabic ? "تعديل قسم" : "Edit Division",
                                deleteDivisions: isArabic ? "حذف قسم" : "Delete Division",
                                // Categories
                                viewCategories: isArabic ? "عرض البراندات" : "View Categories",
                                addCategories: isArabic ? "إضافةبراند" : "Add Category",
                                editCategories: isArabic ? "تعديلبراند" : "Edit Category",
                                deleteCategories: isArabic ? "حذفبراند" : "Delete Category",
                                // Customers
                                viewCustomers: isArabic ? "عرض الزبائن" : "View Customers",
                                addCustomers: isArabic ? "إضافة زبون" : "Add Customer",
                                editCustomers: isArabic ? "تعديل زبون" : "Edit Customer",
                                deleteCustomers: isArabic ? "حذف زبون" : "Delete Customer",
                                // Banners
                                viewBanners: isArabic ? "عرض البانرات" : "View Banners",
                                addBanners: isArabic ? "إضافة بانر" : "Add Banner",
                                editBanners: isArabic ? "تعديل بانر" : "Edit Banner",
                                deleteBanners: isArabic ? "حذف بانر" : "Delete Banner",
                                // Theme
                                viewTheme: isArabic ? "عرض الثيم" : "View Theme",
                                editTheme: isArabic ? "تعديل الثيم" : "Edit Theme",
                                // Store Settings
                                viewStoreSettings: isArabic ? "عرض إعدادات المتجر" : "View Store Settings",
                                editStoreSettings: isArabic ? "تعديل إعدادات المتجر" : "Edit Store Settings",
                                // Coupons
                                viewCoupons: isArabic ? "عرض الكوبونات" : "View Coupons",
                                addCoupons: isArabic ? "إضافة كوبون" : "Add Coupon",
                                editCoupons: isArabic ? "تعديل كوبون" : "Edit Coupon",
                                deleteCoupons: isArabic ? "حذف كوبون" : "Delete Coupon",
                                // Activity
                                viewActivity: isArabic ? "عرض سجل الإجراءات" : "View Activity",
                                // Subscriptions
                                viewSubscriptions: isArabic ? "عرض الاشتراكات" : "View Subscriptions",
                                editSubscriptions: isArabic ? "تعديل الاشتراكات" : "Edit Subscriptions",
                                // Notification
                                viewNotification: isArabic ? "عرض الإشعارات" : "View Notifications",
                                addNotification: isArabic ? "إضافة إشعار" : "Add Notification",
                                // Payment Methods
                                viewPaymentMethods: isArabic ? "عرض طرق الدفع" : "View Payment Methods",
                                addPaymentMethods: isArabic ? "إضافة طريقة دفع" : "Add Payment Method",
                                editPaymentMethods: isArabic ? "تعديل طريقة دفع" : "Edit Payment Method",
                                deletePaymentMethods: isArabic ? "حذف طريقة دفع" : "Delete Payment Method",
                                // Shipping
                                viewShipping: isArabic ? "عرض الشحن" : "View Shipping",
                                addShipping: isArabic ? "إضافة شحن" : "Add Shipping",
                                editShipping: isArabic ? "تعديل شحن" : "Edit Shipping",
                                deleteShipping: isArabic ? "حذف شحن" : "Delete Shipping",
                                // Invoice
                                viewInvoice: isArabic ? "عرض الفاتورة" : "View Invoice",
                                editInvoice: isArabic ? "تعديل الفاتورة" : "Edit Invoice",
                                // Analytics
                                viewAnalytics: isArabic ? "عرض التحليلات" : "View Analytics",
                                // Sales Report
                                viewSalesReport: isArabic ? "عرض تقرير المبيعات" : "View Sales Report",
                                // Inventory Report
                                viewInventoryReport: isArabic ? "عرض تقرير المخزون" : "View Inventory Report",
                                // Customer Report
                                viewCustomerReport: isArabic ? "عرض تقرير العملاء" : "View Customer Report",
                                // Product Report
                                viewProductReport: isArabic ? "عرض تقرير المنتجات" : "View Product Report",
                                // Employees
                                viewEmployees: isArabic ? "عرض الموظفين" : "View Employees",
                                addEmployees: isArabic ? "إضافة موظف" : "Add Employee",
                                editEmployees: isArabic ? "تعديل موظف" : "Edit Employee",
                                deleteEmployees: isArabic ? "حذف موظف" : "Delete Employee",
                              };

                              // تحديد ما إذا كانت هذه الصلاحية يجب تعطيلها
                              const isMainPermission = section.mainPermission === perm;
                              const shouldDisable = section.mainPermission && !isMainPermission && formData[section.mainPermission] === 0;

                              return (
                                <div key={perm} className="flex items-center justify-between">
                                  <label
                                    className="font-medium"
                                    style={{
                                      color: isDark ? "#f1f5f9" : "#111827",
                                      opacity: shouldDisable ? 0.5 : 1
                                    }}
                                  >
                                    {permLabels[perm] || perm}
                                  </label>
                                  <button
                                    onClick={() => togglePermission(perm)}
                                    disabled={shouldDisable}
                                    className="relative w-10 h-5 rounded-full transition-colors"
                                    style={{
                                      backgroundColor: formData[perm] === 1 ? colors.mainColor : (isDark ? "#475569" : "#d1d5db"),
                                      opacity: shouldDisable ? 0.5 : 1,
                                      cursor: shouldDisable ? "not-allowed" : "pointer"
                                    }}
                                  >
                                    <span
                                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${formData[perm] === 1 ? "translate-x-5" : "translate-x-0"
                                        }`}
                                    />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Modal Footer */}
              <div
                className="sticky bottom-0 flex justify-end gap-3 p-6 border-t"
                style={{
                  borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "#e5e7eb",
                  backgroundColor: isDark ? "#0F172A" : "#ffffff",
                  backdropFilter: isDark ? "blur(10px)" : "none"
                }}
              >
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-lg font-medium transition"
                  style={{
                    backgroundColor: isDark ? "#1E293B" : "#e5e7eb",
                    color: isDark ? "#f1f5f9" : "#374151",
                    border: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid #d1d5db"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = isDark ? "#334155" : "#d1d5db";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = isDark ? "#1E293B" : "#e5e7eb";
                  }}
                >
                  {isArabic ? "إلغاء" : "Cancel"}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg text-white font-medium shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: colors.mainColor }}
                >
                  {saving
                    ? (isArabic ? "جاري الحفظ..." : "Saving...")
                    : (isArabic ? "حفظ التغييرات" : "Save Changes")
                  }
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}